/**
 * ButtonPdf：点击打开全局 PDF 预览弹窗。
 *
 * Attributes:
 * @param {String} data-pdf-url PDF 链接
 *
 * @example
 * ```html
 * <script src="{{ 'component-button-pdf.js' | asset_url }}" defer="defer"></script>
 * <button-pdf data-pdf-url="https://cdn.shopify.com/s/files/1/xxxx/files/demo.pdf">
 *   View PDF
 * </button-pdf>
 * ```
 */
if (!customElements.get('button-pdf')) {
  class ButtonPdf extends HTMLElement {
    static modalId = 'GlobalPdfPreviewModal';
    static lastFocusedElement = null;
    static isGlobalEventsBound = false;

    get _pdfUrl() {
      return this.getAttribute('data-pdf-url');
    }

    constructor() {
      super();
      this._onClick = this.handleClick.bind(this);
      this._onKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', this.getAttribute('tabindex') || '0');
      this.setAttribute('aria-haspopup', 'dialog');
      this.setAttribute('aria-controls', ButtonPdf.modalId);
      this.setAttribute('aria-expanded', this.getAttribute('aria-expanded') || 'false');

      if (!this.classList.contains('pdf-preview-trigger')) {
        this.classList.add('pdf-preview-trigger');
      }

      ButtonPdf.ensureModal();
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);

      ButtonPdf.bindGlobalEvents();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
    }

    handleClick(event) {
      event.preventDefault();
      this.openPreview();
    }

    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.click();
      }
    }

    openPreview() {
      var modal = ButtonPdf.getModal();
      var frame = ButtonPdf.getFrame();
      var image = ButtonPdf.getImage();
      var isImageUrl = ButtonPdf.isImageUrl(this._pdfUrl);
      var prefersImage = Boolean(this.querySelector('img')) || isImageUrl;

      if (!modal || !frame || !image || !this._pdfUrl) {
        return;
      }

      ButtonPdf.lastFocusedElement = document.activeElement;
      ButtonPdf.resetExpandedState();

      if (prefersImage) {
        modal.classList.add('is-image');
        frame.setAttribute('src', '');
        image.setAttribute('src', this._pdfUrl);
      } else {
        modal.classList.remove('is-image');
        image.setAttribute('src', '');
        frame.setAttribute('src', this._pdfUrl);
      }
      modal.classList.add('is-active');
      modal.style.display = 'flex';
      document.body.classList.add('pdf-preview-modal-open');
      this.setAttribute('aria-expanded', 'true');

      var modalBox = modal.querySelector('.pdf-preview-modal__box-inner');
      if (modalBox) {
        modalBox.focus();
      }
    }

    static ensureModal() {
      if (ButtonPdf.getModal() || !document.body) {
        return;
      }

      var modal = document.createElement('div');
      modal.className = 'pdf-preview-modal';
      modal.id = ButtonPdf.modalId;
      modal.setAttribute('data-pdf-preview-modal', '');
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'PDF preview');
      modal.innerHTML = ''
        + '<div class="pdf-preview-modal__overlay" data-pdf-preview-close></div>'
        + '<div class="pdf-preview-modal__box">'
        + '  <div class="pdf-preview-modal__box-inner" tabindex="-1">'
        + '    <iframe class="pdf-preview-modal__frame" data-pdf-preview-frame title="PDF preview" loading="lazy"></iframe>'
        + '    <img class="pdf-preview-modal__image" data-pdf-preview-image alt="" loading="lazy">'
        + '  </div>'
        + '</div>';

      document.body.appendChild(modal);
    }

    static getModal() {
      return document.querySelector('[data-pdf-preview-modal]');
    }

    static getFrame() {
      return document.querySelector('[data-pdf-preview-frame]');
    }

    static getImage() {
      return document.querySelector('[data-pdf-preview-image]');
    }

    static isImageUrl(url) {
      return /\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i.test(String(url || ''));
    }

    static resetExpandedState() {
      document.querySelectorAll('button-pdf[aria-expanded="true"]').forEach(function (element) {
        element.setAttribute('aria-expanded', 'false');
      });
    }

    static closePreview() {
      var modal = ButtonPdf.getModal();
      var frame = ButtonPdf.getFrame();
      var image = ButtonPdf.getImage();

      if (!modal || !frame || !image) {
        return;
      }

      modal.classList.remove('is-active');
      modal.style.display = 'none';
      modal.classList.remove('is-image');
      document.body.classList.remove('pdf-preview-modal-open');
      frame.setAttribute('src', '');
      image.setAttribute('src', '');
      ButtonPdf.resetExpandedState();

      if (ButtonPdf.lastFocusedElement && document.contains(ButtonPdf.lastFocusedElement)) {
        ButtonPdf.lastFocusedElement.focus();
      }
    }

    static bindGlobalEvents() {
      if (ButtonPdf.isGlobalEventsBound) {
        return;
      }

      document.addEventListener('click', function (event) {
        if (event.target.closest('[data-pdf-preview-close]')) {
          ButtonPdf.closePreview();
        }
      });

    //   document.addEventListener('keydown', function (event) {
    //     var modal = ButtonPdf.getModal();

    //     if (event.key === 'Escape' && modal && modal.classList.contains('is-active')) {
    //       ButtonPdf.closePreview();
    //     }
    //   });

      ButtonPdf.isGlobalEventsBound = true;
    }
  }

  customElements.define('button-pdf', ButtonPdf);
}
